import React from "react";
import { Link } from "react-router-dom";
import { AlarmClock, AlertOctagon, ChevronRight, MessageCircle, X } from "lucide-react";
import usePlatformWa, { buildWaLink } from "../lib/usePlatformWa";

/**
 * 5-day trial renewal reminder banner (also stays visible after expiry).
 *
 * Props:
 *   - usage: { days_left, expired, plan_id, trial_ends, show_renewal_reminder }
 *   - shopName: optional; used in the wa.me support draft
 *
 * Dismissal is per-session via sessionStorage (key includes days_left + expired
 * state, so it re-appears the next day or once expired).
 */
function buildSupportDraft(shopName, daysLeft, expired) {
  const name = shopName || "Meri shop";
  return expired
    ? `Assalam-o-Alaikum, HisaabPro team. ${name} ka plan expire ho gaya hai. Renewal mein help chahiye.`
    : `Assalam-o-Alaikum, HisaabPro team. ${name} ka plan ${daysLeft} din mein expire ho raha hai. Renewal mein help chahiye.`;
}

export default function TrialRenewalBanner({ usage, shopName }) {
  const platformWa = usePlatformWa();
  const dismissKey = usage
    ? `renewal_dismissed:${usage.expired ? "expired" : "d" + usage.days_left}`
    : null;
  const [dismissed, setDismissed] = React.useState(() =>
    dismissKey ? sessionStorage.getItem(dismissKey) === "1" : false
  );

  if (!usage || !usage.show_renewal_reminder || dismissed) return null;

  const { days_left, expired, plan_id } = usage;
  const tone = expired
    ? {
        wrap: "bg-red-50 border-red-300 ring-1 ring-red-200",
        icon: "text-red-700",
        title: "text-red-800",
        body: "text-red-700",
        cta: "bg-red-600 hover:bg-red-700 text-white",
        ghost: "text-red-700 border-red-300 hover:bg-red-100",
      }
    : days_left <= 1
    ? {
        wrap: "bg-amber-50 border-amber-300 ring-1 ring-amber-200",
        icon: "text-amber-700",
        title: "text-amber-900",
        body: "text-amber-800",
        cta: "bg-amber-600 hover:bg-amber-700 text-white",
        ghost: "text-amber-800 border-amber-300 hover:bg-amber-100",
      }
    : {
        wrap: "bg-amber-50 border-amber-200",
        icon: "text-amber-700",
        title: "text-amber-900",
        body: "text-amber-800",
        cta: "bg-emerald-600 hover:bg-emerald-700 text-white",
        ghost: "text-amber-800 border-amber-300 hover:bg-amber-100",
      };

  const Icon = expired ? AlertOctagon : AlarmClock;
  const title = expired
    ? "Plan expire ho chuka hai"
    : days_left <= 0
    ? "Plan aaj expire ho raha hai"
    : days_left === 1
    ? "Plan kal expire ho raha hai"
    : `Plan ${days_left} din mein expire ho raha hai`;

  const body = expired
    ? "Abhi renew karein taki aap ki shop bina rukavat chalti rahe. 2 minute mein bank/JazzCash/Easypaisa par payment karein, screenshot upload karein — admin foran verify kar dega."
    : "Service bina rukavat chalti rahe, isliye abhi renew kar lein. 2 minute mein payment karein, screenshot upload karein — admin foran verify kar dega.";

  const onDismiss = () => {
    if (dismissKey) sessionStorage.setItem(dismissKey, "1");
    setDismissed(true);
  };

  return (
    <div
      data-testid="trial-renewal-banner"
      data-expired={expired ? "1" : "0"}
      data-days-left={String(days_left)}
      className={`rounded-md border p-4 sm:p-5 relative ${tone.wrap}`}
    >
      <div className="flex items-start gap-3">
        <div className={`shrink-0 mt-0.5 ${tone.icon}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-display font-bold text-base ${tone.title}`}>
            {title}
          </div>
          <div className={`text-sm mt-0.5 ${tone.body}`}>{body}</div>
          <div className="text-[11px] uppercase tracking-widest mt-2 font-semibold text-slate-500">
            Current plan: <span className="capitalize">{plan_id}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Link
              to="/subscription"
              data-testid="trial-renew-cta"
              className={`inline-flex items-center gap-1 rounded-md text-sm font-bold px-3.5 py-2 transition ${tone.cta}`}
            >
              Renew now <ChevronRight className="w-4 h-4" />
            </Link>
            <a
              href={buildWaLink(platformWa.support_number, buildSupportDraft(shopName, days_left, expired))}
              target="_blank"
              rel="noreferrer"
              data-testid="trial-renew-support"
              className={`inline-flex items-center gap-1.5 rounded-md border text-sm font-semibold px-3 py-2 transition ${tone.ghost}`}
            >
              <MessageCircle className="w-4 h-4" /> Support par WhatsApp karein
            </a>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          data-testid="trial-renew-dismiss"
          className="shrink-0 text-slate-400 hover:text-slate-700 p-1 -mt-1 -mr-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
